import { model as i18n } from '!/i18n'
import { model as cartModel } from '$/cart'
import { model as paymentInfoModel, type UiPaymentInfo } from '$/payment-info'
import {
  getPriceSettingsExternalPaymentData,
  model as priceSettingsModel,
} from '$/price-settings'
import { PriceSettingsPaymentMethod, api } from '@setplex/tria-api'
import { createEvent, sample } from 'effector'
import { model as catchupsModel } from '~/entities/catchups'
import { nay } from '~/shared/helpers'
import type {
  CatchupFormatted,
  CatchupProgramFormatted,
} from '~/shared/interfaces'
import { anyPaymentInitiated } from '.'
import { getPaymentTvChannelMediaType, isPaid } from '../../lib'
import * as external from './external'

export const selectCatchupPaymentEvent = createEvent<{
  catchup: CatchupFormatted
  program: CatchupProgramFormatted
}>()
const selectForInternalPayment = createEvent<CatchupFormatted>()

export const payForCatchup = createEvent<CatchupFormatted>()

sample({
  clock: selectCatchupPaymentEvent,
  source: external.$externalPaymentEnabled,
  filter: (external, { catchup }) => external && isPaid(catchup.channel),
  fn: (_, { catchup, program }) => ({
    id: catchup.channel.id,
    mediaType: catchup.channel.priceSettings
      ? getPaymentTvChannelMediaType(catchup.channel.priceSettings)
      : null,
    params: {
      channelId: catchup.channel.id,
      catchupId: catchup.id,
      programmeId: program.id,
    },
    ...getPriceSettingsExternalPaymentData(
      catchup.channel.priceSettings,
      catchup.channel.externalId
    ),
  }),
  target: external.selectForExternalPayment,
})

sample({
  clock: selectCatchupPaymentEvent,
  source: external.$externalPaymentEnabled,
  filter: (external, { catchup }) => !external && isPaid(catchup.channel),
  fn: (_, { catchup }) => catchup,
  target: selectForInternalPayment,
})

sample({
  clock: catchupsModel.selectCathchupProgram,
  filter: ({ catchup }) =>
    nay(catchup.channel.free) && nay(catchup.channel.purchaseInfo),
  target: selectCatchupPaymentEvent,
})

sample({
  clock: selectForInternalPayment,
  target: anyPaymentInitiated,
})

sample({
  clock: selectForInternalPayment,
  filter: (catchup) => !catchup.channel.free && !catchup.channel.purchaseInfo,
  fn: (catchup) => catchup,
  target: payForCatchup,
})

sample({
  clock: payForCatchup,
  fn: ({ channel }) => channel.priceSettings ?? null,
  target: priceSettingsModel.$priceSettings,
})

sample({
  clock: payForCatchup,
  source: {
    selectedPriceSettings: priceSettingsModel.$selectedPriceSettings,
    i18n: i18n.$t,
  },
  filter: ({ selectedPriceSettings }) =>
    selectedPriceSettings?.paymentMethod === PriceSettingsPaymentMethod.PerItem,
  fn: ({ i18n: { t } }, catchup) => {
    return {
      name: catchup.channel.name,
      title: t('live_channel'),
      channelPosterUrl: catchup.channel.logoUrl,
      labels: null,
      priceSettings: catchup.channel.priceSettings,
    } as UiPaymentInfo
  },
  target: paymentInfoModel.$uiPaymentInfo,
})

sample({
  clock: payForCatchup,
  source: {
    selectedPriceSettings: priceSettingsModel.$selectedPriceSettings,
    bundle: api.tvBundles.$tvBundleWithContent,
    i18n: i18n.$t,
  },
  filter: ({ selectedPriceSettings }) =>
    selectedPriceSettings?.paymentMethod === PriceSettingsPaymentMethod.PerSet,
  fn: ({ selectedPriceSettings, bundle, i18n: { t } }, catchup) => {
    const itemCountLabel = t('live_channel_number', {
      count: bundle?.contentQuantity,
    })
    return {
      name: selectedPriceSettings?.bundle?.name,
      title: t('extras'),
      imageUrl: '',
      labels: [itemCountLabel],
      priceSettings: catchup.channel.priceSettings,
      bundlePoster: true,
    } as UiPaymentInfo
  },
  target: paymentInfoModel.$uiPaymentInfo,
})

sample({
  clock: payForCatchup,
  source: { selectedPriceSettings: priceSettingsModel.$selectedPriceSettings },
  filter: ({ selectedPriceSettings }) =>
    selectedPriceSettings?.paymentMethod === PriceSettingsPaymentMethod.PerItem,
  fn: (_, catchup) => ({
    catchupId: catchup.id,
    channelId: catchup.channel.id,
  }),
  target: cartModel.$tvChannelInfoForCart,
})

sample({
  clock: payForCatchup,
  source: { selectedPriceSettings: priceSettingsModel.$selectedPriceSettings },
  filter: ({ selectedPriceSettings }) =>
    selectedPriceSettings?.paymentMethod === PriceSettingsPaymentMethod.PerSet,
  fn: ({ selectedPriceSettings }) => ({
    bundleId: selectedPriceSettings?.bundle?.id as number,
  }),
  target: api.tvBundles.getTvBundleInfoFx,
})

sample({
  clock: api.tvBundles.getTvBundleInfoFx.done,

  fn: ({ result }) => ({
    channelSetId: result.id,
    channelIds: result.channelIds,
  }),
  target: cartModel.$tvBundleInfoForCart,
})
